// src/pages/Admin/Adherents/components/ManageAdherentGroupsDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaCheck,
  FaLayerGroup,
  FaSave,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";

import AdherentDrawer from "./AdherentDrawer";

import {
  addAdherentToGroup,
  removeAdherentFromGroup,
} from "../../../../../services/adherentServices";

function getGroupPillClasses(group) {
  const color = String(group?.color_groupe || "").trim();

  if (color.startsWith("bg-")) {
    return `${color} text-slate-900 ring-1 ring-black/5 dark:ring-white/10`;
  }

  return "bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";
}

function getAdherentName(adherent) {
  const nom = String(adherent?.nom || "").trim().toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
}

export default function ManageAdherentGroupsDrawer({
  isOpen,
  onClose,
  adherent,
  groups = [],
  refreshList,
}) {
  const adherentId = adherent?.id;
  const adherentGroups = Array.isArray(adherent?.Groups) ? adherent.Groups : [];

  const [filter, setFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  const nameToId = useMemo(() => {
    const map = new Map();

    groups.forEach((group) => {
      const key = String(group?.nom || "").trim().toLowerCase();

      if (key) {
        map.set(key, String(group.id));
      }
    });

    return map;
  }, [groups]);

  const currentIds = useMemo(() => {
    return new Set(
      adherentGroups
        .map((group) => {
          if (group?.id != null) return String(group.id);

          const key = String(group?.nom || "").trim().toLowerCase();

          return nameToId.get(key);
        })
        .filter(Boolean)
    );
  }, [adherentGroups, nameToId]);

  useEffect(() => {
    if (!isOpen) return;

    setFilter("");
    setSelectedIds(new Set(currentIds));
    setSubmitting(false);
  }, [isOpen, currentIds]);

  const sortedGroups = useMemo(() => {
    return groups
      .slice()
      .sort((a, b) =>
        String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr")
      );
  }, [groups]);

  const filteredGroups = useMemo(() => {
    const query = filter.trim().toLowerCase();

    if (!query) return sortedGroups;

    return sortedGroups.filter((group) => {
      const name = String(group?.nom || "").toLowerCase();
      const dojo = String(group?.dojo_nom || group?.Dojo?.dojo_nom || "").toLowerCase();

      return name.includes(query) || dojo.includes(query);
    });
  }, [sortedGroups, filter]);

  const selectedGroups = useMemo(() => {
    const map = new Map(groups.map((group) => [String(group.id), group]));

    return Array.from(selectedIds)
      .map((id) => map.get(String(id)))
      .filter(Boolean)
      .sort((a, b) =>
        String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr")
      );
  }, [groups, selectedIds]);

  const toAdd = useMemo(() => {
    return Array.from(selectedIds)
      .filter((id) => !currentIds.has(id))
      .map(Number);
  }, [selectedIds, currentIds]);

  const toRemove = useMemo(() => {
    return Array.from(currentIds)
      .filter((id) => !selectedIds.has(id))
      .map(Number);
  }, [selectedIds, currentIds]);

  const toggle = (groupId) => {
    const key = String(groupId);

    setSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (submitting) return;

    if (!adherentId) {
      toast.error("ID adhérent manquant.");
      return;
    }

    try {
      setSubmitting(true);

      await Promise.all([
        ...toAdd.map((groupId) => addAdherentToGroup(adherentId, groupId)),
        ...toRemove.map((groupId) => removeAdherentFromGroup(adherentId, groupId)),
      ]);

      toast.success(
        `Groupes mis à jour : ${toAdd.length} ajout(s), ${toRemove.length} retrait(s).`
      );

      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur mise à jour groupes :", error);
      toast.error("Erreur lors de la mise à jour des groupes.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdherentDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Gérer les groupes"
      subtitle={
        adherent
          ? `Associer les groupes de ${getAdherentName(adherent)}.`
          : "Associez l'adhérent à un ou plusieurs groupes."
      }
      width="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
                Sélectionnés
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
                {selectedIds.size}
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
                À ajouter
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
                {toAdd.length}
              </p>
            </div>

            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
                À retirer
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
                {toRemove.length}
              </p>
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
            <div className="mb-3 flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                <FaSearch />
              </span>

              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">
                  Rechercher un groupe
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  Filtrez par nom de groupe ou dojo.
                </p>
              </div>
            </div>

            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                type="text"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder="Nom du groupe ou dojo..."
                className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                  <FaLayerGroup />
                </span>

                <div>
                  <h3 className="font-bold text-slate-900 dark:text-white">
                    Groupes disponibles
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Cochez ou décochez les groupes à associer.
                  </p>
                </div>
              </div>

              <span className="rounded-2xl bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {filteredGroups.length}
              </span>
            </div>

            <div className="grid max-h-[420px] gap-2 overflow-auto pr-1 sm:grid-cols-2">
              {filteredGroups.length === 0 ? (
                <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
                  Aucun groupe ne correspond à votre recherche.
                </div>
              ) : (
                filteredGroups.map((group) => {
                  const checked = selectedIds.has(String(group.id));
                  const dojoName = group?.dojo_nom || group?.Dojo?.dojo_nom || "";

                  return (
                    <label
                      key={group.id}
                      className={`flex cursor-pointer items-center gap-3 rounded-2xl border px-3 py-3 transition ${
                        checked
                          ? "border-indigo-200 bg-indigo-50 ring-1 ring-indigo-200 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:ring-indigo-500/20"
                          : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-900"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(group.id)}
                        className="h-5 w-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex rounded-full px-2 py-1 text-xs font-bold ${getGroupPillClasses(
                              group
                            )}`}
                          >
                            {group.nom}
                          </span>

                          {checked ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20">
                              <FaCheck />
                              Sélectionné
                            </span>
                          ) : null}
                        </div>

                        {dojoName ? (
                          <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                            Dojo : {dojoName}
                          </p>
                        ) : null}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
            <h3 className="mb-3 flex items-center gap-2 font-bold text-slate-900 dark:text-white">
              <FaCheck className="text-emerald-500" />
              Groupes sélectionnés
            </h3>

            {selectedGroups.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
                Aucun groupe sélectionné.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {selectedGroups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => toggle(group.id)}
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold transition hover:opacity-80 ${getGroupPillClasses(
                      group
                    )}`}
                    title="Cliquer pour retirer"
                  >
                    {group.nom}
                    <FaTimes size={10} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-slate-800 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {submitting ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </form>
    </AdherentDrawer>
  );
}